// adapter.js — il monolite index.html come "modulo procurement v0" (design §7).
// La shell possiede rail e sottomenù; il monolite possiede renderer, store, auth,
// api e le 26 viste: qui NON si riscrive nulla, si registra e si incapsula (WRAP).
//
// GUARDIA KILL-SWITCH: l'adapter si registra SOLO nella shell v2 (Workspaces.version===2
// e navigate presente). Sotto workspace-host (bundle shell rimosso/404) resta INERTE:
// wsh creerebbe un bottone rail "Proc" rotto per ogni register — mai registrare lì.
//
// R3 (design §7.1/§7.2): submenu:'shell' + nav[] da nav-items.js (26 viste, B2.12),
// shell:navigate con PARENT map (i detail attivano la voce padre), openDeepLink su
// VIEW_IDS, badge nav:badge (Centro Azioni + Conversazioni) riletti dagli stessi
// dati del monolite a ogni data:changed/shell:navigate.
//
// Testato con node --test (server/src/lib/shell-proc-adapter.test.js).

import { proxyProc } from '../shell/legacy-bridge.js';
import { buildNav, PARENT, ALL_VIEW_IDS } from './nav-items.js';

const VIEW_IDS = new Set(ALL_VIEW_IDS);

// Badge del monolite, riletti dagli STESSI dati che alimentano i contatori della
// sidebar legacy (#ac-counter: index.html:16434; .conv-counter: index.html:16445).
// L'adapter è il proprietario del contratto monolite (V3): può leggere i suoi globals.
function computeBadges(win) {
  let ac = 0;
  let conv = 0;
  try {
    if (win.store && typeof win.store.computeActionTasks === 'function') {
      ac = win.store.computeActionTasks().length || 0;
    }
  } catch (e) { /* store non pronto: badge a 0, si riprova al prossimo evento */ }
  try {
    conv = ((win.DB && win.DB.conversations) || [])
      .filter((c) => c && c.lastDirection === 'in' && c.state !== 'closed' && !c.archived).length;
  } catch (e) { /* come sopra */ }
  return { 'action-center': ac, conversations: conv };
}

// F3.3 (ondata 16): dall'Action Hub al RECORD. I deep-link del Cruscotto portano
// params {from:'dash-actions', kind, id, supplierId, campaignId, productId}; dove il
// monolite espone GIÀ una via programmatica al record — gli stessi opener che usano
// i suoi click (openConversationModal:14793, currentSupplierId+'supplier-detail',
// currentCampaignId+'campaign-detail', openSendQueueModal:14359, _whTab di
// renderWarehouse) — il click atterra sul record. Regole:
//  - SOLO per i deep-link dell'Action Hub (from==='dash-actions'): la navigazione
//    da sottomenù/boot resta byte-per-byte quella di prima;
//  - le fonti del Centro Azioni NON si deviano (itemId 'action-center'): la coda
//    È la loro vista-record (ogni card ha il suo modale di contesto);
//  - opener assente (build vecchia del monolite) ⇒ fallback onesto alla vista.
// win.showView si legge a CALL-TIME: è la versione WRAPPATA (emette shell:navigate,
// quindi il sottomenù shell si allinea anche sugli atterraggi record).
// Ritorna true se il record è stato aperto.
function openActionRecord(win, itemId, params) {
  const p = params || {};
  if (p.from !== 'dash-actions' || !p.id || itemId === 'action-center') return false;
  const sv = typeof win.showView === 'function' ? win.showView : null;
  if (!sv) return false;
  const kind = p.kind || '';
  if (kind === 'conversation' && typeof win.openConversationModal === 'function') {
    sv('conversations', {});                 // la lista resta sotto al modale (chiudi ⇒ contesto giusto)
    win.openConversationModal(p.id);
    return true;
  }
  if (kind === 'supplier') {
    win.currentSupplierId = p.id;            // pattern del monolite (es. proc-v0.js:6629)
    sv('supplier-detail', {});
    return true;
  }
  if (kind === 'quotation' && p.supplierId) {
    win.currentSupplierId = p.supplierId;    // il record-padre: le quotazioni si gestiscono nel dettaglio fornitore
    sv('supplier-detail', {});
    return true;
  }
  if (kind === 'campaign_supplier' && p.campaignId) {
    win.currentCampaignId = p.campaignId;    // l'arco fornitore×campagna vive nel dettaglio campagna
    sv('campaign-detail', {});
    return true;
  }
  if (kind === 'send_queue' && typeof win.openSendQueueModal === 'function') {
    sv('campaigns', {});
    win.openSendQueueModal(p.campaignId ? { campaignId: p.campaignId } : {});
    return true;
  }
  if (kind === 'ddt') {
    win._whTab = 'ddt';                      // renderWarehouse legge window._whTab: tab "DDT & Partite"
    sv('warehouse', {});
    return true;
  }
  return false; // nessuna via record per questo kind: si atterra sulla vista (mappa F3.3)
}

export function installProcV0(win) {
  const W = win.Workspaces;
  if (!W || W.version !== 2 || typeof W.navigate !== 'function' || !W.bus) return false;
  const bus = W.bus;

  W.register({
    id: 'proc',
    version: 2,
    submenu: 'shell', // R3: sottomenù a resa unica della shell (nav-items.js)
    nav: buildNav(),
    label: 'Procurement', // nella shell vince comunque il glossario (B2.17)
    // i renderer restano SUOI (§7.1): la shell chiede, showView esegue.
    // F3.3: prima si tenta l'atterraggio sul RECORD (solo deep-link dash-actions).
    navigate: (itemId, params) => {
      if (openActionRecord(win, itemId, params)) return;
      if (typeof win.showView === 'function') win.showView(itemId, params || {});
    },
    // Deep-link (§7.1): vista censita ⇒ showView e true; ignoto ⇒ false (la shell
    // farà toast da R5/B2.18 — mai silenzio lato adapter).
    openDeepLink: (link) => {
      if (!link || typeof link.item !== 'string') return false;
      if (!VIEW_IDS.has(link.item)) return false;
      if (typeof win.showView !== 'function') return false;
      win.showView(link.item, link.params || {});
      return true;
    },
    onShow: () => { proxyProc(win.document); },
  });

  // Emissione badge — solo al cambiamento (niente spam sul bus).
  let lastBadges = {};
  function emitBadges() {
    const next = computeBadges(win);
    for (const item of Object.keys(next)) {
      if (lastBadges[item] === next[item]) continue;
      try { bus.emit('nav:badge', { module: 'proc', item, count: next[item] }); } catch (e) { console.error(e); }
    }
    lastBadges = next;
  }

  // Incapsulamenti prescritti dal protocollo 2.3 — WRAP, non modifica.
  // showView → shell:navigate con PARENT map (§7.2: il detail attiva la voce padre);
  // 'view' porta la vista RAW (serve al dispatch del deep-link al boot: #/proc/suppliers
  // deve forzare la LISTA anche se il monolite ha ripristinato un detail della stessa voce).
  if (typeof win.showView === 'function' && !win.showView.__shellProcWrapped) {
    const _sv = win.showView;
    const wrapped = function (name, opts) {
      const r = _sv.apply(this, arguments);
      try { bus.emit('shell:navigate', { module: 'proc', item: PARENT[name] || name, view: name }); } catch (e) { console.error(e); }
      try { emitBadges(); } catch (e) { console.error(e); } // rerenderView ha appena aggiornato i contatori legacy
      return r;
    };
    wrapped.__shellProcWrapped = true;
    win.showView = wrapped;
  }
  // rerenderAll → data:changed (base dei badge R2+ e delle action card F3).
  if (typeof win.rerenderAll === 'function' && !win.rerenderAll.__shellProcWrapped) {
    const _rr = win.rerenderAll;
    const wrappedRr = function () {
      const r = _rr.apply(this, arguments);
      try { bus.emit('data:changed', { source: 'proc' }); } catch (e) { console.error(e); }
      return r;
    };
    wrappedRr.__shellProcWrapped = true;
    win.rerenderAll = wrappedRr;
  }

  // Badge: primo giro subito (replay del bus per chi nasce dopo) + a ogni
  // data:changed + un giro periodico allineato al poller conversazioni del
  // monolite (index.html:16454 aggiorna i contatori legacy ogni 6s SENZA passare
  // da rerenderAll: senza questo giro il badge shell resterebbe stantio).
  emitBadges();
  bus.on('data:changed', () => { emitBadges(); }, { replay: false });
  if (typeof win.setInterval === 'function') win.setInterval(emitBadges, 6000);

  return true;
}
